import React, { useState, useEffect } from "react";
import { Menu, X, Languages, ArrowRight } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const LOGO_URL =
  "https://jaytech.id/images/logo.png";

export default function Navbar() {
  const { t, lang, toggleLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.whyUs },
    { id: "industries", label: t.nav.industries },
    { id: "contact", label: t.nav.contact },
  ];

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#home"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Jaytech home"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm overflow-hidden ring-1 ring-white/5">
              <img
                src={LOGO_URL}
                alt="Jaytech"
                className="h-10 w-10 object-cover"
                loading="eager"
              />
            </span>
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-heading font-extrabold text-[15px] tracking-tight text-slate-900">
                JAYTECH
              </span>
              <span className="text-[10px] tracking-[0.22em] text-slate-500 uppercase mt-0.5">
                Informatika Indonesia
              </span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-testid={`nav-link-${item.id}`}
                className="text-sm font-medium text-slate-600 hover:text-[#1E5BD6] transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#1E5BD6] hover:after:w-full after:transition-all"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={toggleLang}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-slate-200 bg-white/70 hover:border-[#1E5BD6]/60 hover:text-[#1E5BD6] text-xs font-semibold text-slate-700 tracking-wider transition-colors"
            >
              <Languages className="h-3.5 w-3.5" />
              <span data-testid="lang-current">{lang === "id" ? "ID" : "EN"}</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-400">{lang === "id" ? "EN" : "ID"}</span>
            </button>

            <a
              href="#contact"
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#1E5BD6] hover:bg-[#164AA8] text-white text-sm font-semibold transition-colors"
            >
              {t.nav.cta}
              <ArrowRight className="h-3.5 w-3.5" />
            </a>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-sm border border-slate-200 text-slate-700"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden glass-header border-t border-slate-200/70"
        >
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={close}
                data-testid={`mobile-nav-link-${item.id}`}
                className="px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-sm"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={close}
              data-testid="mobile-nav-cta"
              className="mt-2 inline-flex justify-center items-center gap-1.5 h-11 px-4 rounded-sm bg-[#1E5BD6] hover:bg-[#164AA8] text-white text-sm font-semibold"
            >
              {t.nav.cta}
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
